import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeMathjax from "rehype-mathjax";
import rehypeHighlight from "rehype-highlight";
import "../styles/AiTutor.scss";
import TeacherGif from "../styles/assets/gif/Ai_tutor_gif.gif";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import ReplayIcon from "@mui/icons-material/Replay";
import InfoIcon from "@mui/icons-material/Info";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import GlobalLoader from "./GlobalLoader";
import Tooltip from "./Tooltip";
import ConversationalAnalysis from "./ConversationalAnalysis";
import CloseIcon from "@mui/icons-material/Close";
import edluminaLogo from "../assets/png/edluminaLogo.png";
import { checkIsAiEvalEnabled, checkIsAdmin } from "../helper/authUtils";
import { getUserAiEvalTutor } from "../helper/apicalls/user";
import AiEvalBreakdownPopup from "./common/AiEvalBreakdownPopup";

const AiTutor = ({
  lessons,
  isLoading,
  error,
  setShowAiTutor,
  pageId,
  pageKey,
  pageTitle,
  aiTutorContentId,
  setAiTutorContentId,
  onRegenerate,
  isRegenerating,
}) => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const [speed, setSpeed] = useState("medium");
  const [isSpeedOpen, setIsSpeedOpen] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [showAssistant, setShowAssistant] = useState(false);
  const speedRef = useRef(null);

  const [tutorEval, setTutorEval] = useState(null);
  const [showEvalPopup, setShowEvalPopup] = useState(false);
  const [evalPollTrigger, setEvalPollTrigger] = useState(0);

  const handleRegenerateClick = async () => {
    setTutorEval(null);
    if (onRegenerate) {
      await onRegenerate();
    }
    setEvalPollTrigger((prev) => prev + 1);
  };

  const parseScore = (val) => {
    if (val === undefined || val === null) return null;
    if (typeof val === "number") return val;
    const parsed = parseFloat(val);
    return isNaN(parsed) ? val : parsed;
  };

  const parseJsonOrObject = (val) => {
    if (!val) return {};
    if (typeof val === "object") return val;
    try {
      return JSON.parse(val);
    } catch (e) {
      return {};
    }
  };

  useEffect(() => {
    if (!aiTutorContentId || !checkIsAiEvalEnabled() || checkIsAdmin()) return;

    let isMounted = true;
    let intervalId = null;
    let attempts = 0;
    const maxAttempts = 10; // make for three time

    const fetchTutorEval = async () => {
      try {
        const res = await getUserAiEvalTutor({ ai_tutor_content_id: aiTutorContentId });
        if (!isMounted) return false;
        const evalItem = res?.evaluation || (res?.evaluations && res.evaluations[aiTutorContentId]);
        if (res && res.success && evalItem) {
          setTutorEval({
            overall_score: parseScore(evalItem.overall_score),
            evalution_score: parseJsonOrObject(evalItem.evalution_score)
          });
          return true;
        }
      } catch (err) {
        console.error("Failed to load tutor AI evaluation:", err);
      }
      return false;
    };

    const startPolling = async () => {
      const found = await fetchTutorEval();
      if (found || !isMounted) return;

      intervalId = setInterval(async () => {
        attempts++;
        if (attempts >= maxAttempts) {
          if (intervalId) clearInterval(intervalId);
          return;
        }
        const success = await fetchTutorEval();
        if (success || !isMounted) {
          if (intervalId) clearInterval(intervalId);
        }
      }, 4000);
    };

    startPolling();

    return () => {
      isMounted = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [aiTutorContentId, evalPollTrigger]);

  const utteranceRef = useRef(null);
  const startTimeRef = useRef(null);
  const slideRefs = useRef([]);
  const audioRef = useRef(null);
  const ttsPausedRef = useRef(false);
  const timerRef = useRef(null);
  const ttsPositionRef = useRef({ charIndex: 0 });

  const [voicesLoaded, setVoicesLoaded] = useState(false);

  const splitWords = (text) => {
    if (!text) return { words: [], offsets: [] };
    const words = text.split(/\s+/).filter(Boolean);
    const offsets = [];
    let pos = 0;
    for (let i = 0; i < words.length; i++) {
      const w = words[i];
      const foundAt = text.indexOf(w, pos);
      offsets.push(foundAt >= 0 ? foundAt : pos);
      pos = (foundAt >= 0 ? foundAt : pos) + w.length;
    }
    return { words, offsets };
  };

  const wordsRef = useRef({});

  const slides = useMemo(() => {
    if (!lessons || !Array.isArray(lessons)) return [];
    if (lessons.length > 0 && typeof lessons[0] === "string") {
      const points = lessons;
      const audioText = points.join(" ");
      return [
        {
          index: 0,
          title: null,
          subtitle: null,
          slide: null,
          points,
          explanation: null,
          audioText,
          audioUrl: null,
        },
      ];
    }
    return lessons.map((lesson, index) => {
      const {
        title,
        subtitle,
        points = [],
        explanation,
        audioTexts = [],
        audioUrl,
      } = lesson;

      const combinedAudioText =
        explanation || audioTexts.filter(Boolean).join(" ");

      return {
        index,
        title,
        subtitle,
        slide: null,
        points,
        explanation,
        audioText: combinedAudioText,
        audioUrl,
      };
    });
  }, [lessons]);

  useEffect(() => {
    const mapping = {};
    slides.forEach((s) => {
      mapping[s.index] = splitWords(s.audioText || "");
    });
    wordsRef.current = mapping;
    ttsPositionRef.current.charIndex = 0;
  }, [slides]);

  const totalSlides = slides.length;
  const hasSlides = totalSlides > 0;

  const currentSlide = hasSlides
    ? slides[Math.min(currentIndex, totalSlides - 1)]
    : null;
  const currentHasAudioUrl = Boolean(currentSlide && currentSlide.audioUrl);

  const supportsSpeech =
    typeof window !== "undefined" && "speechSynthesis" in window;

  const getRateForSpeed = (value) => {
    switch (value) {
      case "slow":
        return 0.75;
      case "medium":
        return 1;
      case "fast":
      default:
        return 1.25;
    }
  };

  const getSamanthaVoice = () => {
    const voices = window.speechSynthesis.getVoices();
    return (
      voices.find((voice) => voice.name === "Samantha") ||
      voices.find((voice) => voice.name.includes("Female")) ||
      voices.find((voice) => voice.lang === "en-US") ||
      null
    );
  };

  useEffect(() => {
    if (!supportsSpeech) return;

    const tryLoad = () => {
      const v = window.speechSynthesis.getVoices();
      if (v && v.length > 0) {
        setVoicesLoaded(true);
      }
    };

    tryLoad();
    window.speechSynthesis.onvoiceschanged = tryLoad;

    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.onvoiceschanged = null;
      }
    };
  }, [supportsSpeech]);

  useEffect(() => {
    if (!isPlaying) return;
    const el = slideRefs.current[currentIndex];
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [currentIndex, isPlaying]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (speedRef.current && !speedRef.current.contains(event.target)) {
        setIsSpeedOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const stopCurrentSpeech = (resetPosition = false) => {
    if (supportsSpeech) {
      try {
        window.speechSynthesis.cancel();
      } catch (e) {}
    }
    if (utteranceRef.current) {
      utteranceRef.current.onend = null;
      utteranceRef.current.onerror = null;
      utteranceRef.current.onboundary = null;
    }
    utteranceRef.current = null;
    ttsPausedRef.current = false;
    if (resetPosition) ttsPositionRef.current.charIndex = 0;
  };

  const getResumeCharIndex = (slideIndex, desiredChar) => {
    const entry = wordsRef.current[slideIndex];
    if (!entry || !entry.offsets || entry.offsets.length === 0) {
      return desiredChar || 0;
    }
    const offsets = entry.offsets;
    const lastOff =
      offsets[offsets.length - 1] +
      (entry.words[entry.words.length - 1]?.length || 0);
    const target = Math.max(0, Math.min(desiredChar || 0, lastOff));
    for (let i = 0; i < offsets.length; i++) {
      if (offsets[i] >= target) return offsets[i];
    }
    return lastOff;
  };

  const speakContinuousFromChar = (index, startCharIndex = 0) => {
    const slide = slides[index];
    if (!slide || !slide.audioText) return;

    if (!wordsRef.current[index])
      wordsRef.current[index] = splitWords(slide.audioText || "");

    const adjustedStart = getResumeCharIndex(index, startCharIndex || 0);

    if (adjustedStart >= slide.audioText.length) {
      const end = Date.now();
      setTimeline((prev) => {
        const idx = prev.findIndex((r) => r.index === index);
        const recordBase = {
          index,
          slide,
          start_time: startTimeRef.current || end,
          end_time: end,
        };
        if (idx >= 0) {
          const copy = [...prev];
          copy[idx] = { ...copy[idx], ...recordBase };
          return copy;
        }
        return [...prev, recordBase];
      });

      setCurrentIndex((prevIdx) => {
        const nextIdx = prevIdx + 1;
        if (nextIdx < totalSlides) {
          setTimeout(() => handlePlaySlide(nextIdx), 300);
          return nextIdx;
        }
        setIsPlaying(false);
        return prevIdx;
      });
      return;
    }

    const remaining = slide.audioText.slice(adjustedStart);
    const utterance = new SpeechSynthesisUtterance(remaining);

    if (voicesLoaded) {
      try {
        const voice = getSamanthaVoice();
        if (voice) {
          utterance.voice = voice;
          if (voice.lang) utterance.lang = voice.lang;
        }
      } catch (e) {}
    }

    utterance.rate = getRateForSpeed(speed);

    utteranceRef.current = utterance;
    const startOffset = adjustedStart;
    try {
      utterance.onboundary = (ev) => {
        if (ev && typeof ev.charIndex === "number") {
          ttsPositionRef.current.charIndex = startOffset + ev.charIndex;
        }
      };
    } catch (e) {}

    utterance.onend = () => {
      ttsPositionRef.current.charIndex = 0;

      const end = Date.now();
      setTimeline((prev) => {
        const idx = prev.findIndex((r) => r.index === index);
        if (idx === -1) return prev;
        const copy = [...prev];
        copy[idx] = { ...copy[idx], end_time: end };
        return copy;
      });

      setCurrentIndex((prevIdx) => {
        const nextIdx = prevIdx + 1;
        if (nextIdx < totalSlides) {
          setTimeout(() => {
            handlePlaySlide(nextIdx);
          }, 300);
          return nextIdx;
        }
        setIsPlaying(false);
        return prevIdx;
      });
    };

    utterance.onerror = () => {
      const end = Date.now();
      setTimeline((prev) => {
        const idx = prev.findIndex((r) => r.index === index);
        if (idx === -1) return prev;
        const copy = [...prev];
        copy[idx] = { ...copy[idx], end_time: end };
        return copy;
      });

      setCurrentIndex((prevIdx) => {
        const nextIdx = prevIdx + 1;
        if (nextIdx < totalSlides) {
          setTimeout(() => {
            handlePlaySlide(nextIdx);
          }, 300);
          return nextIdx;
        }
        setIsPlaying(false);
        return prevIdx;
      });
    };

    try {
      window.speechSynthesis.speak(utterance);
    } catch (err) {}
  };

  useEffect(() => {
    if (!isPlaying) return;

    if (currentHasAudioUrl && audioRef.current) {
      audioRef.current.playbackRate = getRateForSpeed(speed);
      return;
    }

    if (supportsSpeech) {
      stopCurrentSpeech(false);
      setTimeout(() => {
        speakContinuousFromChar(
          currentIndex,
          ttsPositionRef.current.charIndex || 0,
        );
      }, 0);
    }
  }, [speed]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (supportsSpeech) {
        window.speechSynthesis.cancel();
      }
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);

      if (supportsSpeech) {
        window.speechSynthesis.cancel();
      }

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      if (utteranceRef.current) {
        utteranceRef.current.onend = null;
        utteranceRef.current.onerror = null;
        utteranceRef.current.onboundary = null;
        utteranceRef.current = null;
      }

      ttsPausedRef.current = false;
      ttsPositionRef.current.charIndex = 0;

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [supportsSpeech]);

  const handlePlaySlide = (index) => {
    if (!hasSlides) return;
    const slide = slides[index];
    if (!slide) return;

    if (slide.audioUrl && audioRef.current) {
      stopCurrentSpeech(true);

      const audioEl = audioRef.current;
      if (audioEl.src !== slide.audioUrl) {
        audioEl.src = slide.audioUrl;
      }

      audioEl.playbackRate = getRateForSpeed(speed);

      if (index !== currentIndex || audioEl.ended) {
        audioEl.currentTime = 0;
      }

      const now = Date.now();
      startTimeRef.current = now;

      setTimeline((prev) => {
        const existingIdx = prev.findIndex((r) => r.index === index);
        const recordBase = { index, slide, start_time: now, end_time: null };
        if (existingIdx >= 0) {
          const copy = [...prev];
          copy[existingIdx] = { ...copy[existingIdx], ...recordBase };
          return copy;
        }
        return [...prev, recordBase];
      });

      audioEl.onended = () => {
        const end = Date.now();
        setTimeline((prev) => {
          const existingIdx = prev.findIndex((r) => r.index === index);
          const recordBase = {
            index,
            slide,
            start_time: startTimeRef.current || end,
            end_time: end,
          };
          if (existingIdx >= 0) {
            const copy = [...prev];
            copy[existingIdx] = { ...copy[existingIdx], ...recordBase };
            return copy;
          }
          return [...prev, recordBase];
        });

        setCurrentIndex((prevIdx) => {
          const nextIdx = prevIdx + 1;
          if (nextIdx < totalSlides) {
            setTimeout(() => {
              handlePlaySlide(nextIdx);
            }, 300);
            return nextIdx;
          }
          setIsPlaying(false);
          return prevIdx;
        });
      };

      audioEl.play();
      return;
    }

    const now = Date.now();
    startTimeRef.current = now;

    setTimeline((prev) => {
      const existingIdx = prev.findIndex((r) => r.index === index);
      const recordBase = { index, slide, start_time: now, end_time: null };
      if (existingIdx >= 0) {
        const copy = [...prev];
        copy[existingIdx] = { ...copy[existingIdx], ...recordBase };
        return copy;
      }
      return [...prev, recordBase];
    });

    if (supportsSpeech && slide.audioText) {
      if (!wordsRef.current[index])
        wordsRef.current[index] = splitWords(slide.audioText || "");

      speakContinuousFromChar(index, ttsPositionRef.current.charIndex || 0);
    } else {
      const end = Date.now();
      setTimeline((prev) => {
        const idx = prev.findIndex((r) => r.index === index);
        if (idx === -1) return prev;
        const copy = [...prev];
        copy[idx] = { ...copy[idx], end_time: end };
        return copy;
      });
      setCurrentIndex((prevIdx) => {
        const nextIdx = prevIdx + 1;
        if (nextIdx < totalSlides) {
          setTimeout(() => {
            handlePlaySlide(nextIdx);
          }, 300);
          return nextIdx;
        }
        setIsPlaying(false);
        return prevIdx;
      });
    }
  };

  const handlePlay = () => {
    if (!hasSlides) return;

    if (currentHasAudioUrl && audioRef.current) {
      const audioEl = audioRef.current;
      if (audioEl.paused && !audioEl.ended) {
        audioEl.play();
        setIsPlaying(true);
        return;
      }
    }

    if (supportsSpeech && ttsPausedRef.current) {
      try {
        window.speechSynthesis.resume();
        ttsPausedRef.current = false;
        setIsPlaying(true);
        return;
      } catch (e) {
        stopCurrentSpeech(false);
        setIsPlaying(true);
        speakContinuousFromChar(
          currentIndex,
          ttsPositionRef.current.charIndex || 0,
        );
        return;
      }
    }

    if (supportsSpeech && window.speechSynthesis.paused) {
      try {
        window.speechSynthesis.resume();
        ttsPausedRef.current = false;
        setIsPlaying(true);
        return;
      } catch (e) {
        stopCurrentSpeech(false);
        setIsPlaying(true);
        speakContinuousFromChar(
          currentIndex,
          ttsPositionRef.current.charIndex || 0,
        );
        return;
      }
    }

    setIsPlaying(true);
    const startingIndex = currentIndex >= totalSlides - 1 ? 0 : currentIndex;
    if (!ttsPausedRef.current && !(ttsPositionRef.current.charIndex > 0)) {
      ttsPositionRef.current.charIndex = 0;
    }
    setCurrentIndex(startingIndex);
    handlePlaySlide(startingIndex);
  };

  const handlePause = () => {
    setIsPlaying(false);

    if (currentHasAudioUrl && audioRef.current) {
      const audioEl = audioRef.current;
      if (!audioEl.paused) {
        audioEl.pause();
      }
      return;
    }

    if (supportsSpeech) {
      if (window.speechSynthesis.speaking && !window.speechSynthesis.paused) {
        ttsPausedRef.current = true;
        try {
          window.speechSynthesis.pause();
        } catch (e) {}
      }
    }
  };

  const handleRestart = () => {
    stopCurrentSpeech(true);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    setTimeline([]);
    setCurrentIndex(0);
    setIsPlaying(true);
    handlePlaySlide(0);
  };

  if (error) {
    return (
      <div className="ai-tutor-wrapper">
        <div className="ai-tutor">
          <button
            type="button"
            className="ai-tutor-close-button"
            onClick={() => setShowAiTutor(false)}
            aria-label="Close AI Tutor"
          >
            ×
          </button>
          <p className="ai-tutor__empty">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {isLoading && <GlobalLoader />}

      <div className="ai-tutor-wrapper">
        <div className="ai-tutor">
          <div className="ai-tutor__header">
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div className="ai-tutor__title-pill">AI Tutor</div>
              {checkIsAiEvalEnabled() && !checkIsAdmin() && (
                <>
                  {tutorEval ? (
                    <button
                      className="ps-row__aieval-chip evaluated"
                      onClick={() => setShowEvalPopup(true)}
                      style={{ margin: 0 }}
                    >
                      Eval: {tutorEval.overall_score}/10
                    </button>
                  ) : (
                    <button
                      className="ps-row__aieval-chip loading"
                      disabled
                      style={{ margin: 0 }}
                    >
                      Eval: Evaluating...
                    </button>
                  )}
                  {onRegenerate && (
                    <button
                      className="ps-row__aieval-chip regenerate"
                      onClick={handleRegenerateClick}
                      disabled={isRegenerating || isLoading}
                      style={{ margin: 0 }}
                      title="Regenerate AI Tutor Content"
                    >
                      {isRegenerating ? "Regenerating..." : "Regenerate"}
                    </button>

                  )}
                </>
              )}
            </div>
            <button
              type="button"
              className="ai-tutor-close-button"
              onClick={() => setShowAiTutor(false)}
              aria-label="Close AI Tutor"
            >
              ×
            </button>
          </div>

          <div className="ai-tutor__frame custom-scrollbar">
            {hasSlides ? (
              slides.map((slide, idx) => (
                <div
                  key={slide.index}
                  ref={(el) => (slideRefs.current[idx] = el)}
                  className={`ai-tutor__slide ${
                    idx === currentIndex ? "ai-tutor__slide--active" : ""
                  }`}
                >
                  <div className="ai-tutor__slide-top">
                    {slide.title && (
                      <div className="ai-tutor__meta-title">
                        <ReactMarkdown
                          remarkPlugins={[remarkMath, remarkGfm]}
                          rehypePlugins={[rehypeMathjax, rehypeHighlight]}
                        >
                          {slide.title.trim()}
                        </ReactMarkdown>
                      </div>
                    )}
                    {slide.subtitle && (
                      <div className="ai-tutor__meta-subtitle">
                        <ReactMarkdown
                          remarkPlugins={[remarkMath, remarkGfm]}
                          rehypePlugins={[rehypeMathjax, rehypeHighlight]}
                        >
                          {slide.subtitle.trim()}
                        </ReactMarkdown>
                      </div>
                    )}
                  </div>

                  {slide.points && slide.points.length > 0 && (
                    <div className="ai-tutor__points-container">
                      <ul className="ai-tutor__points">
                        {slide.points.map((pt, pIdx) => (
                          <ReactMarkdown
                            key={pIdx}
                            remarkPlugins={[remarkMath, remarkGfm]}
                            rehypePlugins={[rehypeMathjax, rehypeHighlight]}
                          >
                            {pt.trim()}
                          </ReactMarkdown>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p className="ai-tutor__empty">No lessons available.</p>
            )}
          </div>

          <div className="ai-tutor__footer-bar">
            <div className="ai-tutor__footer-controls">
              <div
                className="assistant-trigger"
                onClick={() => setShowAssistant(!showAssistant)}
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <img
                  src={edluminaLogo}
                  height={40}
                  className="assistant-image"
                  alt="Edlumina Assistant"
                />
              </div>

              <Tooltip content="Restart" position="top">
                <button
                  type="button"
                  onClick={handleRestart}
                  className="ai-tutor__footer-icon-btn"
                  aria-label="Restart"
                >
                  <ReplayIcon />
                </button>
              </Tooltip>

              <Tooltip content={isPlaying ? "Pause" : "Play"} position="top">
                <button
                  type="button"
                  onClick={isPlaying ? handlePause : handlePlay}
                  className="ai-tutor__play-pause-btn"
                  aria-label={isPlaying ? "Pause" : "Play"}
                >
                  {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                </button>
              </Tooltip>

              <div className="speed-dropdown-wrapper" ref={speedRef}>
                <Tooltip
                  content="Playback Speed"
                  position="top"
                  disabled={isSpeedOpen}
                >
                  <button
                    type="button"
                    className="ai-tutor__speed-btn"
                    onClick={() => setIsSpeedOpen(!isSpeedOpen)}
                  >
                    {speed === "slow"
                      ? "0.75x"
                      : speed === "medium"
                        ? "1x"
                        : "1.25x"}
                    <KeyboardArrowDownIcon />
                  </button>
                </Tooltip>
                {isSpeedOpen && (
                  <div className="ai-tutor__speed-dropdown">
                    {["slow", "medium", "fast"].map((option) => (
                      <button
                        key={option}
                        onClick={() => {
                          setSpeed(option);
                          setIsSpeedOpen(false);
                        }}
                      >
                        {option === "slow"
                          ? "0.75x"
                          : option === "medium"
                            ? "1x"
                            : "1.25x"}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button
                type="button"
                className="ai-tutor__explain-main-btn"
                onClick={() => setShowExplanation(!showExplanation)}
              >
                Explanation <InfoIcon />
              </button>
            </div>
          </div>

          {showExplanation &&
            currentSlide &&
            (currentSlide.explanation || currentSlide.audioText) && (
              <div className="ai-tutor__explanation-panel">
                <div className="ai-tutor__explanation-header">
                  <h3>Explanation</h3>
                  <button
                    type="button"
                    onClick={() => setShowExplanation(false)}
                    className="ai-tutor__explanation-close"
                    aria-label="Close Explanation"
                  >
                    ×
                  </button>
                </div>
                <div className="ai-tutor__explanation-content custom-scrollbar">
                  <ReactMarkdown
                    remarkPlugins={[remarkMath, remarkGfm]}
                    rehypePlugins={[rehypeMathjax, rehypeHighlight]}
                  >
                    {(
                      currentSlide.explanation || currentSlide.audioText
                    ).trim()}
                  </ReactMarkdown>
                </div>
              </div>
            )}

          <audio ref={audioRef} />

          <img
            src={TeacherGif}
            alt="AI Tutor"
            className="ai-tutor__teacher-gif"
            onClick={() => {
              if (currentSlide?.explanation || currentSlide?.audioText) {
                setShowExplanation(!showExplanation);
              }
            }}
            style={{
              cursor:
                currentSlide?.explanation || currentSlide?.audioText
                  ? "pointer"
                  : "default",
            }}
          />
        </div>
      </div>

      {showAssistant && (
        <div
          className="assistant-popup"
          style={{
            position: "fixed",
            bottom: "100px",
            right: "20px",
            width: "350px",
            height: "450px",
            zIndex: 1000,
          }}
        >
          <div className="assistant-header">
            <h3>Ainstein</h3>
            <button
              className="close-btn"
              onClick={() => setShowAssistant(false)}
            >
              <CloseIcon fontSize="small" />
            </button>
          </div>
          <div className="assistant-body">
            <ConversationalAnalysis
              question="Welcome to Ainstein what is your doubt?"
              contentConvo={true}
              apiQueryParams={{
                id: pageId || "",
                key: pageKey || "",
                title: pageTitle || "test",
              }}
            />
          </div>
        </div>
      )}

      <AiEvalBreakdownPopup
        open={showEvalPopup}
        onClose={() => setShowEvalPopup(false)}
        evalData={tutorEval}
      />
    </>
  );
};

export default AiTutor;
